import { useRouter } from 'next/router';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { ApolloProvider, useMutation, useLazyQuery } from '@apollo/client';
import qs from 'qs';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { LoadUserAgentsResponse } from 'apolloClient/types/Agents';
import { LOAD_USER_AGENTS } from 'apolloClient/queries/agents';
import { CREATE_ACTIVITY } from 'apolloClient/mutations/activities';
import {
  client,
  createServerClient,
  setLogoutHandler,
} from 'apolloClient/client';

import SurvicateTracker from 'components/SurvicateTracker';
// CCookie tracker disabled base on decision from 28.04.2026 with Illa gor GTM implementation. CookieYes blocking GTM, to fix that as we not in EU, Illia will add it via GTM, so we can remove it from codebase. For more details please check with Illia or check the ticket https://condoblackbook.atlassian.net/browse/CBBRR-4235
// import CookieTracker from 'components/CookieTraker';

import {
  getAuthDataFromCookies,
  getAuthDataFromSessionStorage,
  isLoginAsUser,
  updateLoginAsUserInCookies,
} from '../src/utils/authUtils';
import {
  saveAgentIdToLocalStorage,
  getAgentDataFromLocalStorage,
  saveAgentDataToLocalStorage,
  isAgentDataExpired,
  ensureBrowserUUID,
  getLeadIdFromLocalStorage,
  saveLeadIdToLocalStorage,
} from '../src/utils/utils';
import { initialAuthData } from 'src/constants/authConstants';

import '../components/RentVsBuyCalculator/Result/RentVsBuyChart.css';
import '../styles/index.css';
import '../styles/pageBuilderParser.css';
import '../components/RentVsBuyCalculator/Result/RentVsBuyChart.css';
import '../components/RentVsBuyCalculator/Result/RentVsBuyChart.css';

import Layout from '../components/layouts/Layout';
import AuthProvider, { AuthContext } from '../components/Auth/AuthProvider';
import ToTheTop from 'components/ToTheTop';
import SavedSearchProvider from 'components/SavedHouses/SavedSearchContext';
import {
  getQueryStringFromAsPath,
  getCommonValuesFromQuery,
} from 'components/Search/utils/searchUtils';
import { stableStringify } from 'src/utils/stableStringify';
import SnackbarProvider from 'components/SnackBar/SnackbarProvider';
import FavoritesListingsProvider from 'components/SavedHouses/SavedFavoritesContext';
import FavoritesBuildingsProvider from 'components/SavedHouses/SavedFavoritesBuildingsContext';
import PopUpNewPassword from 'components/Auth/PopUpNewPassword';
import HeaderProvider from '../components/layouts/Header/HeaderProvider';
import HeaderSearchProvider from '../components/layouts/Header/HeaderSearchProvider';
import PPCProvider from 'components/PPCProvider';

import { useRouteLoader } from '../hooks/useRouteLoader';
import RouteLoader from '../components/RouteLoader';
import { pushContentGroup } from 'src/gtm';
import { resolveContentGroupFromUrl } from 'src/gtm/getContentGroupFromPath';

// Closed on first render, so nothing to server-render; loading it after
// hydration keeps its phone library (libphonenumber-js) out of every page's
// first-load JS.
const PopUpJoinOrSignIn = dynamic(
  () => import('components/Auth/PopUpJoinOrSignIn'),
  { ssr: false }
);

const getPageCodeAndCategory = (params: {
  blog: string | undefined;
  unit: string | undefined;
  building: string | undefined;
  path: string | undefined;
}): { code: string; category: string; isDataSearch: boolean } => {
  if (params.blog) {
    return {
      code: params.blog,
      category: 'blog',
      isDataSearch: false,
    };
  }

  if (params.unit) {
    return {
      code: params.unit,
      category: 'unit',
      isDataSearch: false,
    };
  }

  if (params.building) {
    return {
      code: params.building,
      category: 'building',
      isDataSearch: false,
    };
  }

  if (params.path) {
    const code =
      params.path === '/'
        ? 'home'
        : params.path.indexOf('search') > 0
        ? 'search'
        : '';

    return {
      category: 'common',
      code: code,
      isDataSearch: params.path.indexOf('/search') === 0,
    };
  }

  return {
    category: 'unknown',
    code: '',
    isDataSearch: false,
  };
};

type Logout = () => void;

type AppoloLogoutSubscriberProps = {
  setLogoutHandler: (p: Logout) => void;
  logout: Logout;
};

const ApolloClientLogoutSubscriber: React.FC<AppoloLogoutSubscriberProps> = ({
  logout,
  setLogoutHandler,
}) => {
  useEffect(() => {
    setLogoutHandler(logout);
  }, [setLogoutHandler, logout]);
  return null;
};

type WrappedAppType = AppProps & {};

function WrappedApp({ Component, pageProps }: WrappedAppType) {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { asPath, query, pathname } = router;
  const pathQuery = useMemo(
    () => qs.parse(getQueryStringFromAsPath(asPath), { arrayLimit: 1000 }),
    [asPath]
  );
  // const [prevPath, setPrevPath] = useState<string | null>(null);
  const prevKeyRef = useRef<string | undefined>(undefined);
  // These two hooks live ABOVE <ApolloProvider> in the tree, so we must pass
  // the singleton explicitly — there's no provider context here yet. Safe:
  // both are lazy and only invoked from client-only code paths
  // (requestIdleCallback / useEffect), so they never write to the singleton
  // cache on SSR.
  const [loadUserAgents, { data: agentsData, loading: agentsLoading }] =
    useLazyQuery<{
      getCustomUserAgents: LoadUserAgentsResponse;
    }>(LOAD_USER_AGENTS, { client });

  const [createActivity] = useMutation(CREATE_ACTIVITY, { client });

  // SSR uses a per-request client that's GC'd with the React tree; CSR keeps
  // the singleton. Memoized so the choice is stable across re-renders.
  const apolloClient = useMemo(() => {
    const isServer = typeof window === 'undefined';

    return isServer ? createServerClient() : client;
  }, []);

  const isLoading = useRouteLoader({
    delay: 200,
    minLoadingTime: 300,
  });

  const authData = useMemo(() => {
    if (typeof window === 'object') {
      return !!getAuthDataFromSessionStorage().jwt
        ? getAuthDataFromSessionStorage()
        : getAuthDataFromCookies(document.cookie);
    } else {
      return initialAuthData;
    }
  }, []);

  useEffect(() => {
    if (!isLoginAsUser()) {
      updateLoginAsUserInCookies(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window !== window.parent) {
      window.parent.postMessage(
        { type: 'route-change', url: window.location.href },
        '*'
      );
    }
  }, [asPath]);

  // On client-side navigation (no window reload) push the destination's
  // content_group before the new page renders, so GTM sees it for the
  // upcoming page_view. Full reloads are handled by the inline script in
  // _document.tsx that runs ahead of the GTM container.
  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      pushContentGroup(resolveContentGroupFromUrl(url, window.location.origin));
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.events]);

  useEffect(() => {
    if (agentsData) {
      try {
        saveAgentDataToLocalStorage(
          JSON.stringify(agentsData.getCustomUserAgents)
        );
      } catch (error) {
        console.error(error);
      }
    }
  }, [agentsData]);

  const registerActivity = useCallback(() => {
    if (!!isLoginAsUser()) return;
    const authenticatedUser = authData?.user;
    const referer = document.referrer;
    const building = query['urlBuildParam']?.toString();
    const mlsUnit = query['urlUnitParam']?.toString();
    const blog = query['urlArticle']?.toString();

    const { code, category, isDataSearch } = getPageCodeAndCategory({
      blog: blog ? blog : asPath.includes('/blog') ? '/' : '',
      unit: mlsUnit,
      building,
      path: asPath,
    });

    const leadId = getLeadIdFromLocalStorage();
    const browserUUID = ensureBrowserUUID();

    const activity = {
      isAuthenticated: authenticatedUser?.id != null,
      pageUrl: asPath || referer,
      pageTitle: !asPath.split('?').includes('forRent=true')
        ? document.title
        : ' Miami FL  Rental Listings ',
      pageUrlReferrer: referer,
      userName: authenticatedUser?.username || undefined,
      fromAgent: pathQuery.agent?.toString(),
      buildingSlug: building,
      mslUnitSlug: mlsUnit,
      blogSlug: blog,
      pageCategory: category,
      pageCode: code,
      isDataSearch,
      leadId: leadId && +leadId,
      browserUUID: browserUUID || undefined,
      searchFilter:
        code === 'search' && isDataSearch
          ? getCommonValuesFromQuery(getQueryStringFromAsPath(asPath))
          : null,
    };

    createActivity({ variables: { data: { ...activity } } }).then(
      ({ data }) => {
        const newLeadId = data?.createActivity?.data?.attributes?.leadId;
        const newAgentId = data?.createActivity?.data?.attributes?.agentId;
        const resultAgentData = getAgentDataFromLocalStorage();
        // Refresh once the cached copy passes its TTL (or was never stamped), so a
        // backend change (e.g. isAgentSep) propagates without a manual cache clear.
        let isAgentDataShouldBeUpdated = isAgentDataExpired();

        if (newLeadId && (!leadId || leadId !== newLeadId + '')) {
          saveLeadIdToLocalStorage(String(newLeadId));
          isAgentDataShouldBeUpdated = true;
        }

        if (!isAgentDataShouldBeUpdated && newAgentId && resultAgentData) {
          try {
            const agentsObject = JSON.parse(resultAgentData);
            const mainAgentId =
              agentsObject?.secondAgent?.id || agentsObject?.commonAgent?.id;
            isAgentDataShouldBeUpdated = newAgentId !== mainAgentId;
          } catch (error) {
            console.error('Error parsing JSON resultAgentData', error);
          }
        }

        if (!resultAgentData || isAgentDataShouldBeUpdated) {
          loadUserAgents({
            variables: {
              leadId: newLeadId + '' || leadId || undefined,
              isForStorage: true,
            },
          });
        }
      }
    );
  }, [asPath, authData.user, createActivity, pathQuery.agent, query]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const executeWhenIdle = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(registerActivity);
    } else {
      timeoutRef.current = setTimeout(registerActivity, 2000);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    try {
      pathQuery.agent && saveAgentIdToLocalStorage(pathQuery.agent as string);
      const excludedParams = new Set([
        'page',
        'sortOption',
        'sw',
        'ne',
        'z',
        'isLink',
      ]);
      const currentPath = asPath.split('?')[0].split('#')[0];

      const filteredQuery = Object.fromEntries(
        Object.entries(query).filter(
          ([key, value]) =>
            !excludedParams.has(key) && typeof value === 'string'
        )
      );

      const parsedQueryObjectString = stableStringify(filteredQuery);

      const key = currentPath + '|' + parsedQueryObjectString;

      if (prevKeyRef.current === key) return;

      prevKeyRef.current = key;
      executeWhenIdle();
    } catch (err) {
      console.warn(
        'Failed to compute activity key or execute activity logic',
        err
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath, query]);

  const isHomePage = asPath === '/';
  const isSearchPage = asPath.includes('search');

  return (
    <AuthProvider authDataFromCookie={authData}>
      <AuthContext.Consumer>
        {({ logout }) => (
          <ApolloClientLogoutSubscriber {...{ logout, setLogoutHandler }} />
        )}
      </AuthContext.Consumer>
      <ApolloProvider client={apolloClient}>
        <SnackbarProvider>
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.RECAPTCHA_PUB_KEY || ''}
          >
            <PPCProvider>
              <SavedSearchProvider>
                <FavoritesListingsProvider>
                  <FavoritesBuildingsProvider>
                    <HeaderProvider>
                      <HeaderSearchProvider>
                        <Layout isHomePage={isHomePage}>
                          <RouteLoader isLoading={isLoading} />
                          <ToTheTop isSearchPage={isSearchPage} />
                          <PopUpJoinOrSignIn />
                          <PopUpNewPassword />
                          <SurvicateTracker currentPage={asPath} />
                          {/* <CookieTracker /> */}
                          <Component {...pageProps} />
                        </Layout>
                      </HeaderSearchProvider>
                    </HeaderProvider>
                  </FavoritesBuildingsProvider>
                </FavoritesListingsProvider>
              </SavedSearchProvider>
            </PPCProvider>
          </GoogleReCaptchaProvider>
        </SnackbarProvider>
      </ApolloProvider>
    </AuthProvider>
  );
}

export default WrappedApp;
